import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl, intlShape } from "react-intl";
import { ArrowPathIcon, ChevronDoubleUpIcon, CreditCardIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Alert, Button, Link, Table, useToggleState } from "@yoast/ui-library";

import AnimatedLoader from "../Loader";
import { PageHeader } from "../PageHeader";
import { PageSection } from "../PageSection";
import { BackButton } from "../BackButton";
import { SubscriptionSection } from "./SubscriptionSection";
import { SubscriptionActionSection } from "./SubscriptionActionSection";
import InvoicesDownloadContainer from "../../containers/InvoicesDownload";
import { UpgradeSubscriptionModal } from "./UpgradeSubscription";
import { ProratedUpgradeModal } from "./ProratedUpgradeSubscription";
import SubscriptionEditModal from "../../containers/SubscriptionEditModal";
import AddSiteToSubscriptionModal, { thingToAddSingular } from "./AddSite";
import formatAmount from "../../functions/currency";
import { messages } from "./messages";
import * as styles from "./styles.module.scss";
import ErrorDisplay from "../../errors/ErrorDisplay";
import { SubscriptionDetailContext } from "../../context/SubscriptionDetailContext";
import { getAccessToken, getUserId } from "shared-frontend/functions/auth";
import { Details } from "../Details";
import { IncludedPlugins } from "./IncludedPlugins";
import { ConnectedSites } from "./ConnectedSites";
import { IncludedCourses } from "./IncludedCourses";
import { ConnectedEmailAccess } from "./ConnectedEmailAccess";
import { IncludedServices } from "./IncludedServices";
import ShowPaymentMethod from "./ShowPaymentMethod";
import { IconButton } from "../IconButton";
import useRequest from "../../reactHooks/useRequest";
import { ModalBlock } from "../TwoYearOffer/SubscriptionRow";

const SubscriptionPage = ( props ) => {
	const [ addSeatModalOpen, setAddSeatModalOpen ]                                                         = useState( false );
	const [ upgradeToBundleModalOpen, setUpgradeToBundleModalOpen ]                                         = useState( false );
	const [ cancelModalOpen, setCancelModalOpen ]                                                           = useState( false );
	const [ paidProratedUpgradeModalOpen, , , openPaidProratedUpgradeModal, closePaidProratedUpgradeModal ] = useToggleState( false );
	const [ biennialSwitchModalOpen, , , openBiennialSwitchModal, closeBiennialSwitchModal ]                = useToggleState( false );

	const {
		subscriptionId,
		subscription,
		request: { reload, isLoading, error },
	} = useContext( SubscriptionDetailContext );

	const userId                      = getUserId();
	const switchToBiennialRequest     = useRequest(
		`Customers/${ userId }/Subscriptions/${ subscriptionId }/enable-biennial-billing`,
		"POST",
		{},
		{},
		false,
	);
	const undoSwitchToBiennialRequest = useRequest(
		`Customers/${ userId }/Subscriptions/${ subscriptionId }/disable-biennial-billing`,
		"POST",
		{},
		{},
		false,
	);

	useEffect( () => {
		if ( undoSwitchToBiennialRequest.data?.success || switchToBiennialRequest.data?.success ) {
			// Ugly but cheap: Reload the page to update the banner state too.
			window.location.reload();
			// If, for whatever reason, the page does not reload, we refresh the subscription page contents only.

			// If the request was successful, we reload the subscription data.
			switchToBiennialRequest.reset();
			undoSwitchToBiennialRequest.reset();
			reload();
		}
	}, [ switchToBiennialRequest.isLoading, undoSwitchToBiennialRequest.isLoading ] );

	const getFormattedStatus = ( status ) => {
		const buttonClass = classNames( styles.status, { [ styles.active ]: status === "active" } );
		return <Button className={ buttonClass } size="small" as="span" disabled={ true }>{ status }</Button>;
	};

	const formatDate = ( date ) => {
		if ( ! date ) {
			return "";
		}

		return <FormattedDate
			value={ new Date( date ) }
			year="numeric"
			month="long"
			day="2-digit"
		/>;
	};

	if ( ! subscription && isLoading ) {
		return <AnimatedLoader />;
	}
	if ( error ) {
		error.type = "error";
		return <ErrorDisplay error={ error } />;
	}

	const credentials            = `?access_token=${ getAccessToken() }`;
	const extendSubscriptionLink = (
		<Link href={ subscription.actions.manualRenewal.url } className={ styles.renewLink }>
			<IconButton variant="secondary">
				<ArrowPathIcon />
				<FormattedMessage
					{ ...messages.extendSubscription }
					values={ { billingTerm: subscription.billingTerm } }
				/>
			</IconButton>
		</Link>
	);

	const switchToAutomaticLink = (
		<Link href={ subscription.actions.switchToAutomatic.url + credentials } target="_blank">
			<Button variant="secondary">
				<FormattedMessage { ...messages.switchToAutomatic } />
			</Button>
		</Link>
	);

	const switchPaymentMethodLink = (
		<Link
			href={ subscription.actions.switchPaymentMethod.url + credentials } className={ styles.renewLink }
			target="_blank"
		>
			<IconButton variant="secondary">
				<CreditCardIcon />
				<FormattedMessage
					{ ...messages.switchPaymentMethod }
					values={ { billingTerm: subscription.billingTerm } }
				/>
			</IconButton>
		</Link>
	);

	const upgradeToBundleActionLink = (
		<IconButton
			variant="secondary"
			className={ styles.renewLink }
			onClick={ () => setUpgradeToBundleModalOpen( true ) }
		>
			<ChevronDoubleUpIcon />
			<FormattedMessage { ...messages.upgradeToBundleLink } />
		</IconButton>
	);

	const paidProratedUpgradeActionLink = (
		<IconButton
			variant="secondary"
			className={ styles.renewLink }
			onClick={ openPaidProratedUpgradeModal }
		>
			<ChevronDoubleUpIcon />
			<FormattedMessage { ...messages.upgradeToBundleLink } />
		</IconButton>
	);

	const addSeatToSubscriptionAction = (
		<IconButton
			variant="secondary"
			className={ styles.renewLink }
			onClick={ () => setAddSeatModalOpen( true ) }
		>
			<PlusIcon />
			{ <FormattedMessage
				{ ...messages.addAnotherToSubscriptionLink }
				values={ { thing: thingToAddSingular( subscription.product ) } }
			/> }
		</IconButton>
	);

	const cancelButton = (
		<Button className={ styles.cancelButton } variant="tertiary" onClick={ () => setCancelModalOpen( true ) }>
			{ props.intl.formatMessage( messages.cancelLink ) }
		</Button>
	);

	const subscriptionDetailLines = [
		{
			order: 1,
			title: props.intl.formatMessage( messages.startDate ),
			value: formatDate( subscription.startDate ),
		},
	];

	subscriptionDetailLines.push( {
		order: 2,
		title: props.intl.formatMessage( subscription.willRenew ? messages.nextBilling : messages.endsOn ),
		value: formatDate( subscription.validUntil ),
	} );

	subscriptionDetailLines.push( {
		order: 3,
		title: props.intl.formatMessage( messages.paymentMethod ),
		value: <ShowPaymentMethod subscriptionId={ subscriptionId } />,
	} );

	const subscriptionActionLines = [];

	if ( subscription.actions.switchToBiennial.isAvailable ) {
		subscriptionActionLines.push( {
			order: 1,
			action: (
				<>
					<Button
						variant="upsell"
						onClick={ openBiennialSwitchModal }
					>
						<FormattedMessage { ...messages.switchToBiennialButton } />
					</Button>
					<p className={ styles.actionButtonDescription }>
						<FormattedMessage { ...messages.switchToBiennialDescription } />
					</p>
					<ModalBlock
						subscription={ { nextPayment: subscription.validUntil } }
						closeModal={ closeBiennialSwitchModal }
						isLoading={ switchToBiennialRequest.isLoading }
						onConfirm={ switchToBiennialRequest.reload }
						isOpen={ biennialSwitchModalOpen } error={ switchToBiennialRequest.error }
					/>
				</>
			),
		} );
	}

	if ( subscription.actions.undoSwitchToBiennial.isAvailable ) {
		subscriptionActionLines.push( {
			order: 1,
			action: (
				<>
					<Button
						variant="secondary"
						onClick={ undoSwitchToBiennialRequest.reload }
						disabled={ undoSwitchToBiennialRequest.isLoading }
						isLoading={ undoSwitchToBiennialRequest.isLoading }
					>
						<FormattedMessage { ...messages.undoSwitchToBiennialButton } />
					</Button>
					<p className={ styles.actionButtonDescription }>
						<FormattedMessage { ...messages.undoSwitchToBiennialDescription } />
					</p>
				</>
			),
		} );
	}

	if ( subscription.actions.addSite.isAvailable ) {
		subscriptionActionLines.push( {
			order: 2,
			action: addSeatToSubscriptionAction,
		} );
	}

	if ( subscription.actions.switchToAutomatic.isAvailable ) {
		subscriptionActionLines.push( { action: switchToAutomaticLink } );
	}

	if ( subscription.actions.switchProduct.isAvailable ) {
		subscriptionActionLines.push( {
			order: 3,
			action: upgradeToBundleActionLink,
		} );
	}

	if ( subscription.actions.proratedUpgradeOptions.isAvailable ) {
		subscriptionActionLines.push( {
			order: 3,
			action: paidProratedUpgradeActionLink,
		} );
	}

	if ( subscription.actions.manualRenewal.isAvailable ) {
		subscriptionActionLines.push( {
			order: 1,
			action: extendSubscriptionLink,
		} );
	}

	if ( subscription.actions.cancel.isAvailable ) {
		subscriptionActionLines.push( {
			order: 5,
			action: cancelButton,
		} );
	}

	if ( subscription.actions.switchPaymentMethod.isAvailable ) {
		subscriptionActionLines.push( {
			order: 4,
			action: switchPaymentMethodLink,
		} );
	}

	return (
		<section className={ styles.subscription }>
			<PageHeader
				title={ { ...messages.title, defaultMessage: subscription.name } }
				message={ { ...messages.subtitle, defaultMessage: subscription.limit + " seat subscription" } }
			>
				<BackButton />
			</PageHeader>
			{ subscription.isProvisioned && (
				<div className={ styles.notification }>
					<Alert as="info">
						{ subscription.allowSiteActions
							? (
								<FormattedMessage
									{ ...messages.provisionedWithSiteManagementNotification }
									values={ { displayName: subscription.provisionerName } }
								/>
							)
							: (
								<FormattedMessage
									{ ...messages.provisionedNotification }
									values={ { displayName: subscription.provisionerName } }
								/>
							)
						}
					</Alert>
				</div>
			) }
			<PageSection title={ messages.includedSectionTitle } className={ styles.includedSection }>
				{ subscription.included.inSelf.plugins.length > 0 &&
					<Details>
						<Details.Summary>
							<span className={ styles.primaryTitle } key="title">
								<FormattedMessage { ...messages.yoastPluginsTitle } />
							</span>
							<span className={ styles.secondaryText } key="subtitle">
								<FormattedMessage
									{ ...messages.yoastPluginsDescription }
									values={ { used: subscription.connectedSites.length, limit: subscription.limit } }
								/>
							</span>
						</Details.Summary>
						<Details.Content className={ classNames( styles.accessContainer, styles.pluginsContainer ) }>
							<ConnectedSites />
							<IncludedPlugins withInstallGuide={ true } />
						</Details.Content>
					</Details>
				}

				{ subscription.included.inSelf.accountServices.length > 0 &&
					<Details>
						<Details.Summary>
							<span className={ styles.primaryTitle } key="title">
								<FormattedMessage { ...messages.accountServicesTitle } />
							</span>
							<span className={ styles.secondaryText } key="subtitle">
								<FormattedMessage { ...messages.yoastCoursesDescription } />
							</span>
						</Details.Summary>
						<Details.Content className={ classNames( styles.accessContainer, styles.pluginsContainer ) }>
							<ul>
								{ subscription.included.inSelf.accountServices.map( ( service ) => (
									<li key={ service.name } className={ styles.visibleList }>
										<span> { service.name } </span>
									</li>
								) ) }
							</ul>
						</Details.Content>
					</Details>
				}

				{ subscription.included.inSelf.invitableServices.length > 0 &&
					<Details>
						<Details.Summary>
							<span className={ styles.primaryTitle } key="title">
								<FormattedMessage { ...messages.docsAddonsTitle } />
							</span>
							<span className={ styles.secondaryText } key="subtitle">
								<FormattedMessage
									{ ...messages.docsAddonsDescription }
									values={ {
										used: subscription.emailInvites.used,
										limit: subscription.emailInvites.limit,
									} }
								/>
							</span>
						</Details.Summary>
						<Details.Content className={ classNames( styles.accessContainer, styles.servicesContainer ) }>
							<ConnectedEmailAccess />
							<IncludedServices withInstallGuide={ true } />
						</Details.Content>
					</Details>
				}
				{ subscription.included.inSelf.courses.length > 0 &&
					<Details>
						<Details.Summary>
							<span className={ styles.primaryTitle } key="title">
								<FormattedMessage { ...messages.yoastCoursesTitle } />
							</span>
							<span className={ styles.secondaryText } key="subtitle">
								<FormattedMessage { ...messages.yoastCoursesDescription } />
							</span>
						</Details.Summary>
						<Details.Content className={ styles.accessContainer }>
							<IncludedCourses />
						</Details.Content>
					</Details>
				}
			</PageSection>
			<SubscriptionSection
				title={ messages.subscriptionDetailsTitle }
				lines={ [
					{
						title: props.intl.formatMessage( messages.subscriptionNumber ),
						value: subscription.subscriptionNumber,
					},
					{
						title: props.intl.formatMessage( messages.subscriptionStatus ),
						value: getFormattedStatus( subscription.status ),
					},
				] }
			/>
			{ ! subscription.isProvisioned && <SubscriptionSection
				title={ messages.paymentDetailsTitle }
				lines={ subscriptionDetailLines.sort( ( a, b ) => a.order - b.order ) }
			/> }
			{ ! subscription.isProvisioned && <SubscriptionActionSection
				title={ messages.subscriptionActionTitle }
				lines={ subscriptionActionLines.sort( ( a, b ) => a.order - b.order ) }
			/> }
			{ subscription.invoices.length > 0 && <PageSection title={ messages.invoicesTitle }>
				<Table>
					<Table.Body>
						<Table.Row>
							<Table.Header>{ props.intl.formatMessage( messages.orderDate ) }</Table.Header>
							<Table.Header>{ props.intl.formatMessage( messages.orderId ) }</Table.Header>
							<Table.Header>{ props.intl.formatMessage( messages.orderItems ) }</Table.Header>
							<Table.Header>{ props.intl.formatMessage( messages.orderTotal ) }</Table.Header>
							<Table.Header>{ props.intl.formatMessage( messages.orderStatus ) }</Table.Header>
							<Table.Header>{ "" }</Table.Header>
						</Table.Row>
						{ subscription.invoices.map( ( invoice, i ) => {
							return (
								<Table.Row key={ `row-${ i }` }>
									<Table.Cell>{ invoice.date }</Table.Cell>
									<Table.Cell>{ invoice.invoiceNumber }</Table.Cell>
									<Table.Cell>{
										invoice.items.map( ( itemName, i ) => {
											return <div key={ `item-${ i }` }>{ itemName } </div>;
										} )
									}</Table.Cell>
									<Table.Cell><FormattedNumber
										value={ formatAmount( invoice.totalAmount ) } style="currency"
										currency={ invoice.currency }
									/></Table.Cell>
									<Table.Cell>{ invoice.status }</Table.Cell>
									<Table.Cell><InvoicesDownloadContainer
										resourceId={ invoice.id }
										type={ invoice.type }
									/></Table.Cell>
								</Table.Row>
							);
						} ) }
					</Table.Body>
				</Table>
			</PageSection>
			}

			<SubscriptionEditModal
				isOpen={ cancelModalOpen }
				onClose={ () => setCancelModalOpen( false ) }
			/>
			<AddSiteToSubscriptionModal
				isOpen={ addSeatModalOpen }
				onClose={ () => setAddSeatModalOpen( false ) }
			/>
			<UpgradeSubscriptionModal
				isOpen={ upgradeToBundleModalOpen }
				onClose={ () => setUpgradeToBundleModalOpen( false ) }
			/>
			<ProratedUpgradeModal
				isOpen={ paidProratedUpgradeModalOpen }
				onClose={ closePaidProratedUpgradeModal }
			/>
		</section>
	);
};

SubscriptionPage.propTypes = {
	intl: intlShape.isRequired,
	onManage: PropTypes.func.isRequired,
};

export default injectIntl( SubscriptionPage );
